import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Spinner,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faArrowLeft,
  faCheckCircle,
  faCopy,
  faQrcode,
} from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";
import { TOKEN_KEY } from "../../redux/actions/auth";
import { SET_TOKEN, LOGIN, CLEAR_MFA } from "../../redux/actions/actionTypes";
import { postApi } from "../../redux/api";
import { Routes } from "../../routes";
import { mainTreasuryModules } from "./user-admin/modules";
import AuthSplitLayout from "../../components/AuthSplitLayout";
import "./signin.css";

const getDefaultRoute = (func) => {
  let allRoutes = [];
  mainTreasuryModules.forEach(m => {
    if(m.subMenu) {
      m.subMenu.forEach(s => allRoutes.push(s))
    } else {
      allRoutes.push(m)
    }
  })
  let foundRoute = allRoutes.find((a) => a.title === func);
  return foundRoute;
};

const MFASetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mfaToken = useSelector((state) => state.auth.mfaToken);
  const mfaUser = useSelector((state) => state.auth.mfaUser);

  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (!mfaToken) {
      navigate("/sign-in");
    }
  }, [mfaToken, navigate]);

  const fetchQrCode = useCallback(async () => {
    if (!mfaUser?.username) return;
    setLoading(true);
    try {
      const resp = await postApi("mfa/setup/totp", { username: mfaUser.username });
      if (resp.success) {
        setQrCode(resp.qr_code);
        setSecret(resp.secret);
      } else {
        toast.error(resp.msg || "Failed to generate QR code");
      }
    } catch {
      toast.error("Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  }, [mfaUser]);

  useEffect(() => {
    fetchQrCode();
  }, [fetchQrCode]);

  const handleCodeDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    if (value && index < 5 && codeInputRefs.current[index + 1]) {
      codeInputRefs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0 && codeInputRefs.current[index - 1]) {
      codeInputRefs.current[index - 1].focus();
    }
    if (e.key === "Enter" && index === 5) {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    const fullCode = codeDigits.join("");
    if (fullCode.length !== 6 || !mfaToken) return;

    setVerifying(true);
    try {
      // Verify TOTP code and mark as setup
      const resp = await postApi("mfa/verify/totp", {
        code: fullCode,
        username: mfaUser?.username,
        setup: true,
      });

      if (resp.success) {
        // Complete MFA and get final JWT
        const finalResp = await postApi("mfa/complete", {
          mfa_token: mfaToken,
          method: "authenticator",
        });

        if (finalResp.success) {
          localStorage.setItem(TOKEN_KEY, finalResp.token);
          dispatch({ type: SET_TOKEN, payload: finalResp.token });
          dispatch({ type: LOGIN, payload: finalResp.user });
          dispatch({ type: CLEAR_MFA });

          toast.success("2FA enabled! Welcome!");
          navigateToDashboard(finalResp.user);
        } else {
          toast.error(finalResp.msg || "Failed to complete authentication");
        }
      } else {
        toast.error(resp.msg || "Invalid code");
        setCodeDigits(["", "", "", "", "", ""]);
        if (codeInputRefs.current[0]) codeInputRefs.current[0].focus();
      }
    } catch {
      toast.error("Connection error");
    } finally {
      setVerifying(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (pasteData.length === 6) {
      setCodeDigits(pasteData.split(""));
      if (codeInputRefs.current[5]) codeInputRefs.current[5].focus();
    }
  };

  const handleBackToLogin = () => {
    dispatch({ type: CLEAR_MFA });
    navigate("/sign-in");
  };

  const navigateToDashboard = (user) => {
    const firstItem = user.functionalities ? user.functionalities.split(",")[0] : null;
    if (firstItem) {
      const route = getDefaultRoute(firstItem);
      if (route) {
        navigate(route.route);
        return;
      }
    }
    navigate(Routes.approval.approvalIndex);
  };

  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success("Secret copied to clipboard");
    }
  };

  return (
    <AuthSplitLayout>
      <Toaster position="top-right" toastOptions={{ duration: 4000, style: { background: "#343a40", color: "#fff" } }} />
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h5 className="mb-0 mt-2">
                    Set Up Two-Factor Authentication
                  </h5>
                  <p className="text-muted small mt-1">
                    Scan the QR code with your authenticator app
                  </p>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <p className="text-muted small">Generating QR code...</p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-4">
                      <Card className="border-0 shadow-sm p-3">
                        <div className="mb-3">
                          <FontAwesomeIcon icon={faQrcode} size="2x" className="text-primary mb-2" />
                          <h6 className="text-muted">Step 1: Scan QR Code</h6>
                          <p className="small text-muted mb-2">
                            Open Google Authenticator, Authy, or any TOTP app
                          </p>
                          {qrCode && (
                            <div className="qr-code-container d-inline-block p-2 bg-white border rounded">
                              <img src={qrCode} alt="QR Code" style={{ width: "180px", height: "180px" }} />
                            </div>
                          )}
                        </div>

                        {secret && (
                          <div className="mt-3 p-2 bg-light rounded">
                            <p className="small text-muted mb-1">Or enter this code manually:</p>
                            <div className="d-flex align-items-center justify-content-center">
                              <code className="me-2 user-select-all" style={{ fontSize: "11px", letterSpacing: "1px" }}>
                                {secret}
                              </code>
                              <Button variant="link" size="sm" className="p-0" onClick={copySecret}>
                                <FontAwesomeIcon icon={faCopy} />
                              </Button>
                            </div>
                          </div>
                        )}
                      </Card>
                    </div>

                    <div className="text-center mb-3">
                      <h6 className="text-muted">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
                        Step 2: Enter 6-Digit Code
                      </h6>
                      <p className="small text-muted">
                        Enter the code shown in your authenticator app
                      </p>
                    </div>

                    <div className="otp-input-group justify-content-center" onPaste={handlePaste}>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <input
                          key={i}
                          ref={(el) => (codeInputRefs.current[i] = el)}
                          type="text"
                          maxLength={1}
                          className="otp-input"
                          value={codeDigits[i] || ""}
                          onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(i, e)}
                          autoFocus={i === 0}
                          autoComplete="one-time-code"
                        />
                      ))}
                    </div>

                    <Button
                      variant="primary"
                      className="w-100 mt-3"
                      onClick={handleVerify}
                      disabled={codeDigits.join("").length !== 6 || verifying}
                    >
                      {verifying ? (
                        <><Spinner animation="border" size="sm" className="me-1" /> Verifying...</>
                      ) : (
                        <><FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Enable & Continue</>
                      )}
                    </Button>

                    <div className="text-center mt-3">
                      <Button
                        variant="link"
                        className="p-0 text-muted small text-decoration-none"
                        onClick={handleBackToLogin}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        Back to sign in
                      </Button>
                    </div>
                  </>
                )}
    </AuthSplitLayout>
  );
};

export default MFASetup;
